import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PricingModel } from '@guryou/core';
import { DateTimePicker, DeleteIcon, HiddenField } from '@guryou/html-components';
import { addMinutes, printTime, toFloatingDisplayDate } from '@guryou/utils/build/dateTimeUtils.datefns';
import { dealTokens, SystemFieldControl, SystemIconButton, SystemNumberInput, SystemPriceInput } from 'components/shared';

// Same visual language as the shared, un-forked original
// (modules/providers/containers/newDesigns/shared/WeekDateItem.js) — this onboard fork
// never had it. That reference lives in a wider two-column layout (a whole content
// column, not a narrower card nested inside onboard's own InfoContainer split), so its
// single 6-column grid row genuinely has room. Copied verbatim here, the same 6
// `minmax()` columns don't fit this component's actual available width and just
// overflow the card instead of shrinking — a `minmax()` floor is a hard minimum, not a
// suggestion. Split into two rows instead: day/duration/price (compact, numeric) on
// top, the two time pickers (the fields that actually need real width to avoid
// overlapping their own calendar icon — the original bug) on their own row below,
// with the delete button alongside them.
const WeekDateItem = ({ day, gyClass, name, handleDeleteDay }) => {
  const { t } = useTranslation();
  const [ls, setLs] = useState({
    id: day?.id,
    price: day?.price,
    startDate: day?.startDate,
    startTime: day?.startTime,
    endTime: day?.endTime,
    duration: +day?.duration,
    dayOfWeek: day?.dayOfWeek,
    dayOfWeekText: day?.dayOfWeekText,
  });

  return (
    <div
      style={{
        marginTop: dealTokens.space[3],
        padding: '14px 16px',
        border: `1px solid ${dealTokens.color.border}`,
        borderRadius: dealTokens.radius.md,
        background: dealTokens.color.surface,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
        <div style={{ flex: '1 1 80px', minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: dealTokens.font.weightMedium, color: dealTokens.color.textMuted, marginBottom: 6 }}>
            {t('GENERAL.day')}
          </div>
          <div
            style={{
              boxSizing: 'border-box',
              minHeight: dealTokens.control.md,
              display: 'flex',
              alignItems: 'center',
              padding: '0 11px',
              border: `1px solid ${dealTokens.color.border}`,
              borderRadius: dealTokens.radius.md,
              background: dealTokens.color.surface,
              fontSize: 13.5,
              color: dealTokens.color.text,
            }}
          >
            {ls.dayOfWeekText}
          </div>
        </div>
        <div style={{ flex: '0.8 1 70px', minWidth: 0 }}>
          <SystemNumberInput
            required
            disabled
            inputProps={{ min: 5, step: 5 }}
            label={t('SERVICES.duration')}
            defaultValue={ls?.duration}
            name={`${name}[duration]`}
            onChange={val => {
              let price = ls.price;
              if (gyClass.model === PricingModel.hour) {
                price = (+val / 60) * gyClass.price;
              }
              setLs(s => ({ ...s, price, duration: +val, endTime: addMinutes(s.startTime, val) }));
            }}
          />
        </div>
        {gyClass?.model !== PricingModel.total && (
          <div style={{ flex: '0.8 1 70px', minWidth: 0 }}>
            <SystemPriceInput value={ls?.price} disabled={gyClass?.model !== PricingModel.custom} label={t('SUBSERVICES.price')} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'end', gap: 14, marginTop: dealTokens.space[3] }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Both pickers are display-only, with the times submitted from `ls` — the
              same shape as newDesigns/shared/WeekDateItem.js. The end field used to
              submit through the picker's own hidden field, whose `printTime` re-applied
              the `toFloatingDisplayDate` correction, so after a start or duration change
              a UTC+2 browser saved the end 2h short (10:00 + 90min stored as 09:30 —
              Issue #1707, never fixed in this onboard copy). The weekly path writes this
              end time straight to the schedule. */}
          <SystemFieldControl label={t('GENERAL.from')} name={`${name}[startTime]`}>
            <DateTimePicker
              value={toFloatingDisplayDate(ls?.startTime)}
              onChange={val => {
                setLs(s => ({ ...s, startTime: val, endTime: addMinutes(val, s.duration) }));
              }}
              margin="none"
              fullWidth
              InputProps={{ className: 'studio-input' }}
            />
            <HiddenField name={`${name}[startTime]`} value={printTime(ls?.startTime) || ''} />
          </SystemFieldControl>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SystemFieldControl label={t('GENERAL.to')} name={`${name}[endTime]`}>
            <DateTimePicker
              key={+ls?.endTime}
              value={toFloatingDisplayDate(ls?.endTime)}
              disableField
              margin="none"
              fullWidth
              InputProps={{ className: 'studio-input' }}
            />
            <HiddenField name={`${name}[endTime]`} value={printTime(ls?.endTime) || ''} />
          </SystemFieldControl>
        </div>
        <SystemIconButton variant="danger" icon={<DeleteIcon />} label={t('ONBOARD.remove')} onClick={() => handleDeleteDay(ls.id)} />
      </div>
      <HiddenField value={ls.price} name={`${name}[price]`}></HiddenField>
      <HiddenField value={ls.id} name={`${name}[id]`}></HiddenField>
      <HiddenField value={ls.dayOfWeek} name={`${name}[dayOfWeek]`}></HiddenField>
      <HiddenField value={ls.duration} name={`${name}[duration]`}></HiddenField>
    </div>
  );
};
const areEqual = (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps);
export default React.memo(WeekDateItem, areEqual);
